import {TelemetryEvent, User as IUser} from "@videoblocks/events-ts/lib/storyblocks/telemetry/TelemetryEvent"
import {EventFactory} from "@videoblocks/events-ts/lib/util/EventFactory"
import {TelemetryType} from "@videoblocks/events-ts/lib/storyblocks/telemetry/TelemetryTypeEnum"
import Client from "../Client"
import {User} from "@videoblocks/events-ts/lib/storyblocks/common/User"
import {TraceContext} from "@videoblocks/events-ts/lib/storyblocks/common/TraceContext"

export default class Telemetry {
  constructor(private client: Client, private user: IUser, private trace?: TraceContext) {}

  public static init(client: Client, user: IUser, trace?: TraceContext): void {
    this.instance = new Telemetry(client, user, trace)
  }

  /**
   * Use to mute Telemetry (in dev environments)
   *
   * Once muted, calls to the Telemetry instance will resolve immediately without publishing events
   */
  public static mute(): void {
    this.instance = new Proxy(this.instance, {
      get: (target, prop, receiver) => {
        if (prop === "produceEvent") {
          return () => Promise.resolve()
        }
        return Reflect.get(target, prop, receiver)
      }
    })
  }

  public static destroy(): void {
    delete this.instance
  }

  /**
   * Increment the number of occurrences for a tag.
   */
  public static async increment(tag: string): Promise<void> {
    return this.getInstance().increment(tag)
  }

  /**
   * Track a number (integer or float) value for a tag. For example, CPU load, purchase price, number of results, etc.
   */
  public static async gauge(tag: string, value: number): Promise<void> {
    return this.getInstance().gauge(tag, value)
  }

  /**
   * Starts a timer for a particular tag, returns a function which stops the timer
   * and sends the elapsed time (in milliseconds) to the telemetry system when called
   *
   * Each timer is completely independent; multiple timers for the same tag can be used simultaneously
   * After timer is stopped by calling the returned function, ignore subsequent calls and log a warning
   */
  public static timer(tag: string): () => Promise<void> {
    return this.getInstance().timer(tag)
  }

  public async increment(tag: string): Promise<void> {
    return this.produceEvent(TelemetryType.INCREMENT, this.normalizeTag(tag))
  }

  public async gauge(tag: string, value: number): Promise<void> {
    return this.produceEvent(TelemetryType.GAUGE, this.normalizeTag(tag), value)
  }

  public timer(tag: string): () => Promise<void> {
    const startMilliseconds = Date.now()
    let hasBeenStopped = false
    return async () => {
      if (!hasBeenStopped) {
        const elapsedMilliseconds = Date.now() - startMilliseconds
        hasBeenStopped = true
        return this.produceEvent(TelemetryType.TIMER, tag, elapsedMilliseconds)
      } else {
        console.warn(`Ignoring attempt to send telemetry timer "${tag}" (started at ${startMilliseconds}) more than once`)
      }
    }
  }

  private normalizeTag(tag: string) {
    return tag
      .toLowerCase()
      .replace(/[\t\s\n]/g, "_")
      .replace(/["'`]/g, "")
  }

  private produceEvent(type: TelemetryType, tag: string, value?: number) {
    const telemetry = new TelemetryEvent()
    telemetry.type = type
    telemetry.tag = tag
    telemetry.value = value || null
    telemetry.meta = EventFactory.meta(this.client.getOrigin(), this.getTrace())
    telemetry.user = this.getUser()
    return this.client.produce(telemetry)
  }

  private getUser(): User {
    const user = new User()
    user.visitorCookieId = this.user.visitorCookieId || null
    user.subscriptionId = this.user.subscriptionId || null
    user.userId = this.user.userId || null
    user.organizationId = this.user.organizationId || null
    return user
  }

  private getTrace(): TraceContext | undefined {
    return this.trace
  }

  private static instance: Telemetry

  private static getInstance() {
    if (!this.instance) {
      throw new Error("Must initialize Telemetry with Telemetry.init()")
    }
    return this.instance
  }
}
